import { useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { userRolEnum } from 'enum/user-rol.enum'
import { IMainState } from 'store/states'
import { PermissionsTypes } from 'enum/permissionsType'
import * as fromCoursesActions from 'store/actions/courses.action'
import * as fromProfilesActions from 'store/actions/lms-profiles.action'
import { PermitableTypes } from 'enum/permissionsTypes.enum'
import { ICourseNameList } from 'models/course.model'
import { IBasicProfile } from 'models/lmsProfile.model'

interface CoursePermission {
  type: PermissionsTypes.course
  selected?: ICourseNameList | null
}

interface ProfilePermission {
  type: PermissionsTypes.profile
  selected?: IBasicProfile | undefined
}

type PermissionGrouped = {
  courses: CoursePermission[]
  profiles: ProfilePermission[]
}

const getSelected = <A extends { id: number }>(
  array: A[],
  id?: number | null
) => array.find(({ id: rowId }) => rowId === id)

const usePermissions = () => {
  const { roles } = useSelector(
    ({ participants: { participant } }: IMainState) => participant
  )

  const { moderator } = userRolEnum
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch({ type: fromCoursesActions.COURSES_GET_NAME_LIST })
    dispatch({ type: fromProfilesActions.LMS_ALL_PROFILES_LIST })
  }, [dispatch])

  const { allProfilesList, loading: profilesLoading } = useSelector(
    ({ lmsProfiles }: IMainState) => lmsProfiles
  )
  const { nameList, loading: coursesLoading } = useSelector(
    ({ courses }: IMainState) => courses
  )

  const { coursePermissions, coursesToShow, profilePermissions } =
    useMemo(() => {
      if (
        roles &&
        roles.roleId === moderator &&
        !coursesLoading &&
        !profilesLoading
      ) {
        const { courses, profiles } = roles.permissions.reduce(
          (prev, { permitableId, permitableType }): PermissionGrouped => {
            if (permitableType === PermitableTypes.LmsProfile) {
              const selected = getSelected(allProfilesList, permitableId)
              return {
                ...prev,
                profiles: [
                  ...prev.profiles,
                  {
                    type: PermissionsTypes.profile,
                    selected: selected,
                  },
                ],
              }
            }
            if (permitableType === PermitableTypes.Course) {
              const selected = getSelected(nameList, permitableId)
              return {
                ...prev,
                courses: [
                  ...prev.courses,
                  {
                    type: PermissionsTypes.course,
                    selected: selected,
                  },
                ],
              }
            }
            return prev
          },
          {
            courses: [],
            profiles: [],
          } as PermissionGrouped
        )

        return {
          coursePermissions: courses,
          coursesToShow: courses.slice(0, 3),
          profilePermissions: profiles,
        }
      }
      return {
        coursePermissions: [] as CoursePermission[],
        coursesToShow: [] as CoursePermission[],
        profilePermissions: [] as ProfilePermission[],
      }
    }, [
      allProfilesList,
      coursesLoading,
      moderator,
      nameList,
      profilesLoading,
      roles,
    ])

  return {
    coursePermissions,
    coursesToShow,
    profilePermissions,
  }
}

export default usePermissions
